import React, { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { Promise } from 'es6-shim'
import { useForm } from 'react-hook-form'
import SearchResult from './SearchResult'
import { searchUsers, searchCompanies, searchCompaniesBySAP, getContractsByNameOrSAP, getScaleTicketsByIdOrName } from '../../services/apiHelper'
import searchIcon from '../../assets/icons/searchWhite.svg'
import { GlobalContext } from '../../services/context'

const SearchView = () => {
  const { trackEvent } = useContext(GlobalContext)
  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      companiesCheckbox: true,
      usersCheckbox: true,
      contractsCheckbox: false
    } 
  })
  const [searchState, setSearchState] = useState('no results')
  const [users, setUsers] = useState([])
  const [companies, setCompanies] = useState([])
  const [contracts, setContracts] = useState([])
  const [scaleTickets, setScaleTickets] = useState([])

  const watchSearch = watch('search', '')
  const watchCompanies = watch('companiesCheckbox', true)
  const watchUsers = watch('usersCheckbox', true)
  const watchContracts = watch('contractsCheckbox', false)
  const watchScaleTickets = watch('scaleTicketsCheckbox', false)

  const onSubmit = useCallback(values => {
    try{
      if(values.search?.length >= 3){
        values.search = values.search.substring(0,1) === '1' && values.search.length === 8 ? '00' + values.search : values.search
        if(values.search.substring(0, 2) === '00' && values.search.length >= 10){
          setSearchState('searching')
          const arrs = [setCompanies, setContracts, setScaleTickets]
          Promise.allSettled([
            watchCompanies && searchCompaniesBySAP(values.search),
            watchContracts && getContractsByNameOrSAP(values.search),
            watchScaleTickets && getScaleTicketsByIdOrName(values.search)
          ]).then(results => {
            results.forEach((result, i) => {
              const setter = arrs[i]
              setter(result.value || [])
              setSearchState(prev => {
                if (result.value && result.value.length) {
                  return 'found'
                } else if (prev === 'found' || prev === 'timeout'){
                  return prev
                } else if (result.value instanceof Error) {
                  return 'timeout'
                }
                return 'no results'
              })
            })
          })
        } else {
          setSearchState('searching')
          const arrs = [setUsers, setCompanies, setContracts, setScaleTickets]
          Promise.allSettled([
            watchUsers && searchUsers(values.search, 'full_name', 1000),
            watchCompanies && searchCompanies(values.search, 1000),
            watchContracts && getContractsByNameOrSAP(values.search),
            watchScaleTickets && getScaleTicketsByIdOrName(values.search)
          ]).then(results => {
            results.forEach((result, i) => {
              const setter = arrs[i]
              setter(result.value || [])
              setSearchState(prev => {
                if (result.value && result.value.length) {
                  return 'found'
                } else if (prev === 'found' || prev === 'timeout'){
                  return prev
                } else if (result.value instanceof Error) {
                  return 'timeout'
                }
                return 'no results'
                  
              })
            })
          })
        }
      } else {
        setSearchState('no results')
      }
    } catch (err){
      console.error('Search failed: ', err)
      setSearchState('no results')
    }
  }, [
    setSearchState, setUsers, setCompanies, setContracts, watchCompanies, watchUsers, watchContracts, watchScaleTickets
  ])

  const prevCompanies = useRef(true)
  useEffect(()=> {
    if(watchCompanies !== prevCompanies.current){
      prevCompanies.current = watchCompanies
      trackEvent(`show_company_results_${watchCompanies ? 'checked' : 'unchecked'}`)
    }
  })
  const prevUsers = useRef(true)
  useEffect(()=> {
    if(watchUsers !== prevUsers.current){
      prevUsers.current = watchUsers
      trackEvent(`show_user_results_${watchUsers ? 'checked' : 'unchecked'}`)
    }
  })
  const prevContracts = useRef(true)
  useEffect(()=> {
    if(watchContracts !== prevContracts.current){
      prevContracts.current = watchContracts
      trackEvent(`show_contract_results_${watchContracts ? 'checked' : 'unchecked'}`)
    }
  })
  const prevScaleTicket = useRef(true)
  useEffect(()=> {
    if(watchScaleTickets !== prevScaleTicket.current){
      prevScaleTicket.current = watchScaleTickets
      trackEvent(`show_scale_tickets_${watchScaleTickets ? 'checked' : 'unchecked'}`)
    }
  })

  const searchDebounce = useRef()
  useEffect(()=> {
    const search = searchObj => {
      onSubmit(searchObj)
    }
    if (watchSearch?.length >= 3) {
      clearTimeout(searchDebounce.current)
      searchDebounce.current = setTimeout(()=> {
        search({ search:watchSearch })
      }, 300)
    } else{
      setSearchState('no results')
    }
  }, [onSubmit, watchSearch])

  const printSearchResults = () => {

    let searchedUsers = []
    if(users && users.length > 0) {
      searchedUsers = users.map(user => {
        return (
          <SearchResult key={user.user_id} type='user' data={user}/>
        )
      })
    }

    let searchedCompanies = []
    if(companies && companies.length > 0) {
      searchedCompanies = companies.map(company => {
        return (
          <SearchResult key={company.company_id} type='company' data={company}/>
        )
      })
    }

    let searchedContracts = []
    if(contracts && contracts.length > 0) {
      searchedContracts = contracts.map(contract => {
        return (
          <SearchResult key={contract.contract_id} type='contract' data={contract}/>
        )
      })
    }

    let searchedScaleTickets = []
    if(scaleTickets && scaleTickets.length > 0) {
      searchedScaleTickets = scaleTickets.map((ticket, index) => {
        return (
          <SearchResult key={ticket.scale_ticket_id+'-'+index} type='ticket' data={ticket}/>
        )
      })
    }
    
    switch (searchState) {
      case 'searching':
        return <span>Searching...</span>
      case 'found':
        return (
          <>
            {searchedUsers.length && watchUsers? 
              <>
                {searchedUsers}
              </>
              : null
            }
            {searchedCompanies.length && watchCompanies? 
              <>
                {searchedCompanies}
              </>
              : null
            }
            {searchedContracts.length && watchContracts? 
              <>
                {searchedContracts}
              </>
              : null
            }
            {searchedScaleTickets.length && watchScaleTickets? 
              <>
                {searchedScaleTickets}
              </>
              : null
            }
          </>
        )
      case 'no results':
        return <span>No results</span>
      case 'timeout':
        return <span>Timeout, please click on search button</span>
      default:
        return null
    }
  }
  const [isUsersCheckbox, setIsUsersCheckbox] = useState(true)
  const[isCompaniesCheckbox, setCompaniesCheckbox] = useState(true)
  const[isContractsCheckbox, setContractsCheckbox] = useState(false)
  const[isScaleTicketsCheckbox, setScaleTicketsCheckbox] = useState(false)
  const checkBoxFunc = e => {
    if(e.target.name === 'usersCheckbox'){
      setIsUsersCheckbox(prev => !prev)
    } else if(e.target.name === 'companiesCheckbox'){
      setCompaniesCheckbox(prev => !prev)
    } else if(e.target.name === 'contractsCheckbox'){
      setContractsCheckbox(prev => !prev)
    } else{
      if(!watchScaleTickets){
        setIsUsersCheckbox(false)
        setCompaniesCheckbox(false)
        setContractsCheckbox(false)
      }
      setScaleTicketsCheckbox(prev => !prev)
    }
  }
  return (
    <div className='p10'>
      <form onSubmit={handleSubmit(onSubmit)} className='df fxdrc aifs'>
        <div className='w100%'>
          <div className='df w100%'>
            <div className='flx1'></div>
            <div className='w20'></div>
            <div className='df fxdrc flx3 mb20'>
              <div className='df w100% mb30'>
                <input aria-label='search' name='search' id='search' ref={register} className='fx1'/>
                <button className='mb0 w60 bgc-green bdnone bdrs-4' onClick={handleSubmit(onSubmit)}>
                  <img src={searchIcon} alt='search icon'/>
                </button>
              </div>
              <div className='h30 mb18'>
                {watchSearch && watchSearch.length >= 3 && <p className='df jcfs m0 p0 fz18 fw600'>{`Search for '${watchSearch}'`}</p>}
              </div>
            </div>
          </div>
          <div className='w100% df h500'>
            <div className='flx1 bd1-s-lightgray bdrs-4'>
              <p className='m14 p0 fw600'>Type</p>
              <div className='pl30'>
                <div className='mb20 mr20 df aic jcfs'>
                  <input className='w15' type='checkbox' name='usersCheckbox' id='usersCheckbox' ref={register()} onChange={checkBoxFunc} checked={isUsersCheckbox ? true : false}/>
                  <label htmlFor='usersCheckbox'>Users</label>
                </div>
                <div className='mb20 mr20 df aic jcfs'>
                  <input className='w15' type='checkbox' id='companiesCheckbox' name='companiesCheckbox' ref={register()} onChange={checkBoxFunc} checked={isCompaniesCheckbox ? true : false}/>
                  <label htmlFor='companiesCheckbox'>Companies</label>
                </div>
                <div className='mb20 mr20 df aic jcfs'>
                  <input className='w15' type='checkbox' id='contractsCheckbox' name='contractsCheckbox' ref={register()} onChange={checkBoxFunc} checked={isContractsCheckbox ? true : false}/>
                  <label htmlFor='contractsCheckbox'>Contracts</label>
                </div>
                <div className='mb20 mr20 df aic jcfs'>
                  <input className='w15' type='checkbox' id='scaleTicketsCheckbox' name='scaleTicketsCheckbox' ref={register()} onChange={checkBoxFunc} checked={isScaleTicketsCheckbox ? true : false}/>
                  <label htmlFor='scaleTicketsCheckbox'>Scale Ticket</label>
                </div>
              </div>
            </div>
            <div className='w20'></div>
            <div className='flx3 pt12 pb12 bd1-s-lightgray bdrs-4 os'>
              {printSearchResults()}
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default SearchView